import React from "react";
import { Mic, RotateCcw, Settings, X } from "lucide-react";
import { createPortal } from "react-dom";

const COPY = {
  microphone: {
    title: "Microfono non autorizzato",
    description: "Per registrare messaggi vocali devi consentire l’uso del microfono.",
    browser: "Tocca il lucchetto o l’icona delle impostazioni accanto all’indirizzo del sito, apri Autorizzazioni, imposta Microfono su Consenti e torna qui.",
    app: "Se usi l’app installata: apri Impostazioni del telefono, scegli App, You Voice, Autorizzazioni e abilita Microfono.",
  },
};

export function PermissionRecoveryDialog({ issue, onClose, onRetry }) {
  const [showInstructions, setShowInstructions] = React.useState(issue?.state === "denied");
  const copy = COPY[issue?.permission] || COPY.microphone;

  React.useEffect(() => {
    if (!issue) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const keydown = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", keydown);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", keydown);
    };
  }, [issue, onClose]);

  React.useEffect(() => {
    setShowInstructions(issue?.state === "denied");
  }, [issue]);

  if (!issue) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4" role="presentation">
      <button type="button" className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose} aria-label="Chiudi recupero autorizzazione" />
      <section className="relative z-[1201] w-[min(92vw,420px)] max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-3xl border-2 border-ink bg-cream p-5 shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="permission-recovery-title" data-testid="permission-recovery-dialog">
        <header className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-white"><Mic size={21}/></span>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-primary">Autorizzazione richiesta</p>
              <h2 id="permission-recovery-title" className="text-xl font-bold">{copy.title}</h2>
            </div>
          </div>
          <button type="button" onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-edge bg-white" aria-label="Chiudi"><X size={20}/></button>
        </header>
        <p className="mt-4 text-sm leading-6 text-ink">{copy.description}</p>
        {showInstructions && (
          <div className="mt-3 space-y-2 rounded-2xl border border-edge bg-white p-4 text-sm leading-5" data-testid="permission-instructions">
            <p><strong>Dal sito:</strong> {copy.browser}</p>
            <p><strong>Dall’app:</strong> {copy.app}</p>
          </div>
        )}
        <div className="mt-5 grid gap-2">
          <button type="button" className="big-cta" onClick={()=>{onClose();void onRetry();}} data-testid="permission-retry">
            <RotateCcw size={17}/> Riprova e consenti
          </button>
          <button type="button" className="w-full rounded-full border border-ink bg-white px-4 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2" onClick={()=>setShowInstructions((value)=>!value)} data-testid="permission-help">
            <Settings size={16}/> {showInstructions ? "Nascondi istruzioni" : "Come attivarlo dalle impostazioni"}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}
